import React from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Camera, Image as ImageIcon, Star, Loader2, Clapperboard } from 'lucide-react';
import { platformLabel } from '@/lib/clipPlatformFormats';

/**
 * "Thumbnails" sidebar category — pick the video or clip to capture a
 * freeze frame from (section 3), then manage the thumbnail candidates
 * already made for that exact source (section 6/7). Capturing itself
 * happens in the main area (`ThumbnailCaptureStage.jsx`); this panel is
 * just the source picker + candidate list + default selection.
 */
export function ThumbnailSourcePanel({
  sources, selectedSourceId, onSelectSource,
  clips, clipsLoading, selectedClipId, onSelectClip,
  candidates, activeDesignId, onSelectCandidate,
  onCaptureNew, isCapturing,
  defaultsForSource, onSetDefault,
}) {
  return (
    <div className="space-y-4" data-testid="thumbnail-source-panel">
      <div>
        <label className="text-[11px] text-[#8A93A6] mb-1 block">Select Video or Clip</label>
        <Select value={selectedSourceId || ''} onValueChange={onSelectSource}>
          <SelectTrigger className="h-8 text-xs bg-white/5 border-white/10" data-testid="thumbnail-source-select"><SelectValue placeholder="Browse Media Library" /></SelectTrigger>
          <SelectContent>
            {sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.title || s.id}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {selectedSourceId && (
        <div>
          <label className="text-[11px] text-[#8A93A6] mb-1 block">Frame source</label>
          {clipsLoading ? (
            <p className="text-xs text-[#8A93A6] flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" />Loading this video's clips…</p>
          ) : (
            <Select value={selectedClipId || '__video__'} onValueChange={(v) => onSelectClip(v === '__video__' ? null : v)}>
              <SelectTrigger className="h-8 text-xs bg-white/5 border-white/10" data-testid="thumbnail-clip-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__video__">Full long-form video</SelectItem>
                {clips.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.output_type?.toUpperCase()})</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button size="sm" variant="outline" className="w-full border-white/10 mt-2" onClick={onCaptureNew} disabled={isCapturing} data-testid="thumbnail-capture-new-button">
            <Camera className="h-3.5 w-3.5 mr-1.5" />{isCapturing ? 'Capturing…' : 'Capture New Frame'}
          </Button>
        </div>
      )}

      {selectedSourceId && (
        <div>
          <p className="text-[11px] uppercase tracking-wide text-[#555E6E] mb-1.5">Thumbnail Candidates ({candidates.length})</p>
          {candidates.length === 0 ? (
            <p className="text-xs text-[#8A93A6] text-center py-4" data-testid="thumbnail-candidates-empty">No thumbnails yet for this {selectedClipId ? 'clip' : 'video'} — capture a frame above.</p>
          ) : (
            <div className="space-y-1.5">
              {candidates.map((d) => {
                const defaultPlatforms = Object.entries(defaultsForSource || {}).filter(([, v]) => v.designId === d.id).map(([p]) => p);
                return (
                  <div key={d.id} onClick={() => onSelectCandidate(d.id)} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${d.id === activeDesignId ? 'border-[#3A7BFF] bg-[#3A7BFF]/10' : 'border-white/10 hover:bg-white/5'}`} data-testid={`thumbnail-candidate-${d.id}`}>
                    <ImageIcon className="h-3.5 w-3.5 text-[#8A93A6] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-white truncate">{d.name}</p>
                      <p className="text-[10px] text-[#8A93A6]">{d.variants.map((v) => platformLabel(v.platform)).join(', ')}</p>
                    </div>
                    {defaultPlatforms.length > 0 ? (
                      <span className="shrink-0 flex items-center gap-1 text-[9px] text-amber-300" title={`Default for ${defaultPlatforms.map(platformLabel).join(', ')}`}><Star className="h-3 w-3 fill-amber-300" />Default</span>
                    ) : (
                      <button onClick={(e) => { e.stopPropagation(); onSetDefault(d.id, d.variants[0]?.id); }} className="shrink-0 text-[9px] text-[#8A93A6] hover:text-white" data-testid={`thumbnail-set-default-${d.id}`}>Set Default</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {!selectedSourceId && (
        <p className="text-xs text-[#8A93A6] text-center py-6 flex flex-col items-center gap-2" data-testid="thumbnail-no-source">
          <Clapperboard className="h-5 w-5 opacity-40" />Pick a video or clip above — from the shared Media Library — to start capturing a thumbnail.
        </p>
      )}
    </div>
  );
}
